package org.solyton.solawi.bid.module.banking.component.form

import androidx.compose.runtime.*
import org.evoleq.change.data.Change
import org.evoleq.change.data.Keep
import org.evoleq.compose.Markup
import org.evoleq.compose.form.Form
import org.evoleq.compose.form.field.Field
import org.evoleq.compose.form.label.Label
import org.evoleq.kotlinx.date.now
import org.evoleq.language.Lang
import org.evoleq.language.subComp
import org.evoleq.language.title
import org.evoleq.math.Source
import org.evoleq.math.emit
import org.evoleq.math.invert
import org.evoleq.math.times
import org.evoleq.uuid.NIL_UUID
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.TextInput
import org.solyton.solawi.bid.module.banking.data.CreditorId
import org.solyton.solawi.bid.module.banking.data.CreditorIdentifierId
import org.solyton.solawi.bid.module.banking.data.creditor.identifier.CreditorIdentifier
import org.solyton.solawi.bid.module.banking.data.legalentity.LegalEntity
import org.solyton.solawi.bid.module.banking.data.legalentity.LegalEntityType
import org.solyton.solawi.bid.module.control.dropdown.Dropdown
import org.solyton.solawi.bid.module.control.dropdown.DropdownStyles
import org.solyton.solawi.bid.module.control.dropdown.SimpleUpDown
import org.solyton.solawi.bid.module.style.form.fieldDesktopStyle
import org.solyton.solawi.bid.module.style.form.formDesktopStyle
import org.solyton.solawi.bid.module.style.form.formLabelDesktopStyle
import org.solyton.solawi.bid.module.style.form.textInputDesktopStyle
import org.solyton.solawi.bid.module.user.component.form.UpsertAddressForm
import org.solyton.solawi.bid.module.user.component.form.upsertAddressFormTexts
import org.solyton.solawi.bid.module.user.data.address.Address
import org.solyton.solawi.bid.module.values.LegalEntityId

@Markup
@Composable
@Suppress("FunctionName")
fun LegalEntityForm(
    inputs: Source<Lang.Block>,
    partyId: LegalEntityId,
    legalEntity: LegalEntity?,
    creditorIdentifier: CreditorIdentifier?,
    setLegalEntity: (LegalEntity, CreditorIdentifier?)->Unit,
) {

    val dropdownStyles = DropdownStyles.modifyContainerStyle { width(100.percent) }

    Form(formDesktopStyle) {
        var nameState by remember { mutableStateOf(legalEntity?.name ?: "") }
        var legalFormState by remember { mutableStateOf(legalEntity?.legalForm ?: "") }
        var legalEntityTypeState by remember { mutableStateOf(legalEntity?.legalEntityType ) }
        var addressState by remember { mutableStateOf(legalEntity?.address ?: Address.default()) }

        var creditorIdState by remember { mutableStateOf(creditorIdentifier?.creditorId) }


        Field(fieldDesktopStyle) {
            Label(
                (inputs * subComp("name") * title).emit(),
                id = "name",
                labelStyle = formLabelDesktopStyle
            )
            TextInput(nameState) {
                id("name")
                style { textInputDesktopStyle() }
                onInput {
                    update(
                        LegalEntityChange(
                            legalEntityId = legalEntity?.legalEntityId ?: LegalEntityId(NIL_UUID),
                            partyId = partyId,
                            name = Change(nameState, it.value) {
                                nameState = it.value
                            },
                            legalForm = Keep(legalFormState),
                            legalEntityType = Keep(legalEntityTypeState),
                            address = Keep(addressState),
                            creditorIdentifierId = creditorIdentifier?.creditorIdentifierId ?: CreditorIdentifierId(NIL_UUID),
                            creditorId = Keep(creditorIdState)
                        )) {
                       legalEntity, creditorIdentifier -> setLegalEntity(legalEntity, creditorIdentifier)
                    }
                }
            }
        }
        Field(fieldDesktopStyle) {
            Label(
                (inputs * subComp("legalForm") * title).emit(),
                id = "legal-form",
                labelStyle = formLabelDesktopStyle
            )
            TextInput(legalFormState) {
                id("legal-form")
                style { textInputDesktopStyle() }
                onInput {
                    update(LegalEntityChange(
                        legalEntityId = legalEntity?.legalEntityId ?: LegalEntityId(NIL_UUID),
                        partyId = partyId,
                        name = Keep(nameState),
                        legalForm = Change(legalFormState, it.value){
                            legalFormState = it.value
                        },
                        legalEntityType = Keep(legalEntityTypeState),
                        address = Keep(addressState),creditorIdentifierId = creditorIdentifier?.creditorIdentifierId ?: CreditorIdentifierId(NIL_UUID),
                        creditorId = Keep(creditorIdState)
                    )) { legalEntity, creditorIdentifier -> setLegalEntity(legalEntity, creditorIdentifier) }
                }
            }
        }
        Field(fieldDesktopStyle) {
            Label(
                (inputs * subComp("legalEntityType") * title).emit(),
                id = "legal-entity-type",
                labelStyle = formLabelDesktopStyle
            )
            val legalEntityTypeOptions: Map<String, LegalEntityType> = mapOf(
                "Natürliche Person" to LegalEntityType.HUMAN,
                "Organisation" to LegalEntityType.ORGANIZATION
            )
            val inverted = legalEntityTypeOptions.invert()
            Dropdown(
                options = legalEntityTypeOptions,
                selected = inverted[legalEntityTypeState?: LegalEntityType.HUMAN],
                iconContent = {open -> SimpleUpDown(open) },
                styles = dropdownStyles
            ) {
                (_, value) -> update(LegalEntityChange(
                    legalEntityId = legalEntity?.legalEntityId ?: LegalEntityId(NIL_UUID),
                    partyId = partyId,
                    name = Keep(nameState),
                    legalForm = Keep(legalFormState),
                    legalEntityType = Change(legalEntityTypeState, value) {
                        legalEntityTypeState = value
                    },
                    address = Keep(addressState),
                    creditorIdentifierId = creditorIdentifier?.creditorIdentifierId ?: CreditorIdentifierId(NIL_UUID),
                    creditorId = Keep(creditorIdState)
                )) { legalEntity, creditorIdentifier -> setLegalEntity(legalEntity, creditorIdentifier) }
            }
        }

        Field(fieldDesktopStyle) {
            Label(
                (inputs * subComp("creditorId") * title).emit(),
                id = "creditor-id",
                labelStyle = formLabelDesktopStyle
            )
            TextInput(creditorIdState?.value?:"") {
                id("creditor-id")
                style { textInputDesktopStyle() }
                onInput {
                    update(LegalEntityChange(
                        legalEntityId = legalEntity?.legalEntityId ?: LegalEntityId(NIL_UUID),
                        partyId = partyId,
                        name = Keep(nameState),
                        legalForm = Keep(legalFormState),
                        legalEntityType = Keep(legalEntityTypeState),
                        address = Keep(addressState),creditorIdentifierId = creditorIdentifier?.creditorIdentifierId ?: CreditorIdentifierId(NIL_UUID),
                        creditorId = Change(creditorIdState, CreditorId(it.value)) {
                            creditorIdState = CreditorId(it.value)
                        }
                    )) { legalEntity, creditorIdentifier -> setLegalEntity(legalEntity, creditorIdentifier) }
                }
            }
        }

        UpsertAddressForm(
            texts = Source{ upsertAddressFormTexts },
            address = legalEntity?.address,
            setAddress = { address ->
                update(LegalEntityChange(
                    legalEntityId = legalEntity?.legalEntityId ?: LegalEntityId(NIL_UUID),
                    partyId = partyId,
                    name = Keep(nameState),
                    legalForm = Keep(legalFormState),
                    legalEntityType = Keep(legalEntityTypeState),
                    address = Change(addressState, address) {
                        addressState = address
                    },
                    creditorIdentifierId = creditorIdentifier?.creditorIdentifierId ?: CreditorIdentifierId(NIL_UUID),
                    creditorId = Keep(creditorIdState),
                )) { legalEntity, creditorIdentifier -> setLegalEntity(legalEntity, creditorIdentifier) }
            }
        )

    }
}

fun update(change: LegalEntityChange, onChange: (LegalEntity, CreditorIdentifier?) -> Unit) {
    try{
        val newLegalEntity = LegalEntity(
            legalEntityId = change.legalEntityId,
            partyId = change.partyId,
            name = change.name.new!!,
            legalForm =change.legalForm.new!!,
            legalEntityType = change.legalEntityType.new!!,
            address = change.address.new!!
        )
        val creditorIdentifier = change.creditorId.new?.let{
            CreditorIdentifier(
                change.creditorIdentifierId,
                change.legalEntityId,
                it,
                now().date,
                validUntil = null,
                isActive = true
            )
        }
        onChange(newLegalEntity, creditorIdentifier)

    } catch (exception: Exception) {
        println(exception)
    } finally {
        change.name.onChange()
        change.legalForm.onChange()
        change.legalEntityType.onChange()
        change.address.onChange()
        change.creditorId.onChange()
    }
}

data class LegalEntityChange(
    val legalEntityId: LegalEntityId,
    val partyId: LegalEntityId,
    val name: Change<String>,
    val legalForm: Change<String>,
    val legalEntityType: Change<LegalEntityType>,
    val address: Change<Address>,
    val creditorIdentifierId: CreditorIdentifierId,
    val creditorId: Change<CreditorId?>,
)
